import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import companyService from '../../modules/companies/services/companyService';

export const fetchCompanies = createAsyncThunk(
  'companies/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const data = await companyService.getAll();
      return data.companies || [];
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load companies');
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'companies/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const data = await companyService.getOne(id);
      return data.company ?? data;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to load company');
    }
  }
);

export const createCompany = createAsyncThunk(
  'companies/create',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await companyService.create(payload);
      return data.company ?? data;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to create company');
    }
  }
);

export const updateCompany = createAsyncThunk(
  'companies/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const data = await companyService.update(id, payload);
      return data.company ?? data;
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to update company');
    }
  }
);

export const deleteCompany = createAsyncThunk(
  'companies/delete',
  async (id, { rejectWithValue }) => {
    try {
      const data = await companyService.delete(id);
      return { id, ...(data || {}) };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to delete company');
    }
  }
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState: {
    list: [],
    listLoading: false,
    listError: null,
    detail: null,
    detailLoading: false,
    detailError: null,
    saving: false,
    saveError: null,
    deleting: false,
    deleteError: null,
  },
  reducers: {
    clearCompanyDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload;
      })
      .addCase(fetchCompanyById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })
      .addCase(createCompany.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createCompany.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      })
      .addCase(updateCompany.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload;
        if (updated?._id) {
          const idx = state.list.findIndex((c) => String(c._id) === String(updated._id));
          if (idx >= 0) state.list[idx] = { ...state.list[idx], ...updated };
          if (state.detail && String(state.detail._id) === String(updated._id)) {
            state.detail = { ...state.detail, ...updated };
          }
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      })
      .addCase(deleteCompany.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.deleting = false;
        const id = action.payload?.id;
        if (id) {
          state.list = state.list.filter((c) => String(c._id) !== String(id));
          if (state.detail && String(state.detail._id) === String(id)) {
            state.detail = null;
          }
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload;
      });
  },
});

export const { clearCompanyDetail, clearSaveError } = companiesSlice.actions;

export const selectCompanies = (state) => state.companies.list;
export const selectCompaniesLoading = (state) => state.companies.listLoading;
export const selectCompanyDetail = (state) => state.companies.detail;
export const selectCompanyDetailLoading = (state) => state.companies.detailLoading;
export const selectCompanySaving = (state) => state.companies.saving;
export const selectCompanyDeleting = (state) => state.companies.deleting;

export default companiesSlice.reducer;
